import type { Request, RequestStatus } from "@/hooks/types";

/**
 * How far a request has actually got, as every surface states it (NAV-46).
 *
 * `RequestStatus` cannot answer that: PENDING covers both an evaluation nobody
 * has started and one whose responsibles are working through it, and those are
 * different facts — "nothing has happened yet" versus "someone is on it" for
 * the requester, a to-do list versus work in flight for the BU admin.
 *
 * The split is **derived, never stored** — the same trade `stepDependencies.ts`
 * makes one level down, for the same reasons. `RequestStatus` is the closed set
 * `firestore.rules` pins the decision path on, so a sixth value would have cost
 * a rules change, a migration of every running request and a decision about who
 * may write it, all to record something the request already implies. The price
 * is that every reader computes it, which is why they all come here.
 *
 * What it is derived from is `subjectVerifiedAt`, which is not a proxy for
 * "started" — it *is* the start (OQ-33(b)). Until the BU admin has checked the
 * subject, the steps are instantiated but nobody has been asked to work them:
 * the responsibles' queue leaves the request out (`useResponsibleRequests`),
 * the sweep neither announces nor chases its steps
 * (`send_step_reminders_use_case`), and approval is refused. Verification is
 * the single write that releases all of it, so it is the one honest answer to
 * "has this started".
 *
 * Only an evaluation has a start to wait for. An access request carries no
 * steps and no verification — there is nothing between arriving and being
 * decided — so it stays PENDING.
 *
 * One edge is deliberate rather than overlooked. A BU admin *may* tick a step
 * before verifying — the rules place no ordering condition on a step update
 * and cannot (no subcollection aggregation) — and such a request still reads
 * NOT_STARTED. That is the honest answer at this level: no responsible has
 * been asked, no reminder fires and approval stays blocked, whatever the admin
 * has ticked for themselves. The alternative would have to read the steps, and
 * the requests list does not load them per row, so the two surfaces would then
 * disagree about the same request — which is the thing this exists to stop.
 * The closed step still shows as closed wherever the steps are drawn.
 */
export type RequestPhase =
  | RequestStatus
  | "NOT_STARTED"
  | "IN_REVIEW"
  | "WITH_APPROVER"
  | "WITH_ACTIVATOR";

/** The fields the phase is derived from — a `Request`, or anything shaped like
 *  enough of one (a test fixture, a projection). */
export type PhasedRequest = Pick<
  Request,
  "type" | "status" | "subjectVerifiedAt" | "route" | "approvalOutcome"
>;

export function requestPhase(request: PhasedRequest): RequestPhase {
  // Everything past the decision is the status itself: an approved evaluation
  // was reviewed whatever its verification says, and a cancelled one stopped
  // being anybody's work.
  if (request.status !== "PENDING") return request.status;
  if (request.type === "EVALUATION") {
    return request.subjectVerifiedAt == null ? "NOT_STARTED" : "IN_REVIEW";
  }
  // A routed access request is pending *with somebody*, and which somebody is
  // the fact the requester actually wants (ADR 0017 decision 4). An approved
  // cost is not access — the request stays PENDING and moves to the activator,
  // so a bare "approved" here would promise something that has not happened.
  if (request.route == null) return "PENDING";
  return currentStage(request) === "APPROVE" ? "WITH_APPROVER" : "WITH_ACTIVATOR";
}

/**
 * Which of the two decisions a routed request is at, independently of who is
 * looking — the same question {@link requestPhase} answers, in the vocabulary
 * of the person about to act rather than of the person waiting.
 *
 * Both read it from here rather than each testing `route` and `approvalOutcome`
 * themselves. Two surfaces disagreeing about a request's stage is the failure
 * this prevents: the queue would offer a decision the detail page says is not
 * due, or the reverse, and only one of them can be right.
 *
 * The BU admin's escape hatch is why this is separate at all — their address is
 * nowhere on the request, so "is it mine?" answers no while "which decision is
 * outstanding?" is exactly what they are stepping in to make.
 */
export function currentStage(
  request: Pick<Request, "route" | "approvalOutcome">,
): "APPROVE" | "ACTIVATE" {
  // The quick route starts at the activator, and the standard one arrives there
  // once its approver has said yes. A REFUSED approval never reaches this
  // question: such a request is DENIED and has no stage.
  return request.route === "APPROVAL" && request.approvalOutcome == null
    ? "APPROVE"
    : "ACTIVATE";
}

/**
 * Which of the two parties refused, on a request that was denied.
 *
 * The reason NAV-60 made the second decision a decision rather than a
 * fulfilment status: *"this was not approved"* and *"we could not set it up"*
 * send the requester to different next steps, and one `DENIED` says neither.
 * Which party refused is the fact this answers; *why* they refused is the
 * reason they wrote, and the approver's grounds are not assumed to be cost
 * (Jira NAV-77). Every surface that explains a denial asks this rather than
 * re-reading the two outcome fields, so they cannot disagree about which
 * refusal it was.
 *
 * `null` for the single-decision path — a BU admin denying an unrouted request
 * — and for a denial that carries neither outcome. The caller then says
 * "denied" without naming a party, which is the honest answer rather than a
 * guessed one: an unrouted request has no approver and no activator, so naming
 * either would invent a participant.
 */
export function refusedBy(
  request: Pick<Request, "status" | "approvalOutcome" | "activationOutcome">,
): "APPROVER" | "ACTIVATOR" | null {
  if (request.status !== "DENIED") return null;
  // Checked in this order because a request only reaches the activator with an
  // APPROVED cost behind it, so the later refusal is the operative one.
  if (request.activationOutcome === "NOT_PROVIDED") return "ACTIVATOR";
  if (request.approvalOutcome === "REFUSED") return "APPROVER";
  return null;
}

/** The phases an undecided request can be in — the five things "Pending"
 *  selects: an evaluation waiting to start or being worked, an access request
 *  with its approver or with its activator, and the unrouted kind that is
 *  simply pending. */
export function isOpenPhase(phase: RequestPhase): boolean {
  return (
    phase === "PENDING" ||
    phase === "NOT_STARTED" ||
    phase === "IN_REVIEW" ||
    phase === "WITH_APPROVER" ||
    phase === "WITH_ACTIVATOR"
  );
}

/** Whether anybody has been asked to work this request yet — false only while
 *  an evaluation is still waiting to be started. The question every requester
 *  surface asks, spelled out so they don't each restate it as a comparison. */
export function evaluationStarted(request: PhasedRequest): boolean {
  return requestPhase(request) !== "NOT_STARTED";
}

/**
 * Who may correct the subject right now — the two open rows of ADR 0018's
 * lifecycle table, as the phase rather than as a re-spelled comparison.
 *
 * They are named here because the two dialogs restating them drifted: the
 * responsible's card omitted the PENDING half, so "Correct the facts" appeared
 * on approved, denied and cancelled evaluations and every save failed behind a
 * generic toast. Both rules already fall out of `requestPhase` exactly —
 * NOT_STARTED *is* "pending evaluation, nobody has verified it", IN_REVIEW *is*
 * "pending evaluation, verified" — so a call site that asks the phase cannot
 * get half of one.
 *
 * Identity is the caller's half and stays there: these say what the *phase*
 * permits, not whether this particular viewer is the requester or one of the
 * responsibles.
 */
export function subjectCorrectableByRequester(request: PhasedRequest): boolean {
  return requestPhase(request) === "NOT_STARTED";
}

/** The other row: once the facts are verified they are the responsibles' to
 *  correct, until the evaluation is decided (NAV-21). */
export function subjectCorrectableByResponsible(
  request: PhasedRequest,
): boolean {
  return requestPhase(request) === "IN_REVIEW";
}

const PHASE_LABEL_KEY: Record<RequestPhase, string> = {
  NOT_STARTED: "common:notStarted",
  IN_REVIEW: "common:inReview",
  PENDING: "common:pending",
  WITH_APPROVER: "common:withApprover",
  WITH_ACTIVATOR: "common:withActivator",
  APPROVED: "common:approved",
  DENIED: "common:denied",
  CANCELLED: "common:cancelled",
  EXPIRED: "common:expired",
};

/** Every phase there is, derived from the label map so it cannot fall behind:
 *  the map is a total `Record<RequestPhase, …>`, so a new phase has to be added
 *  there or nothing compiles. What lets a caller iterate the set — the pill's
 *  colours and the tests both do — without a second hand-kept list. */
export const REQUEST_PHASES = Object.keys(PHASE_LABEL_KEY) as RequestPhase[];

/** The i18n key naming a phase, namespaced so it resolves from any surface.
 *
 *  These are the admin-facing words, and the requester's cards deliberately
 *  keep their own ("Not started yet", "Under evaluation") — that wording
 *  predates this and reads better to somebody who filed one request, not a
 *  queue of them. What has to match across roles is the *distinction*, not the
 *  vocabulary, and both sides derive it from `requestPhase`. */
export function requestPhaseLabelKey(phase: RequestPhase): string {
  return PHASE_LABEL_KEY[phase];
}

/**
 * The requester's word for each phase, in the `endUser` bundle.
 *
 * A second vocabulary for the same seven phases, deliberately: the admin set
 * above is queue language ("Pending", "In review") and this one is the words a
 * person reads on their own single request ("In progress", "Under evaluation").
 * What has to match across roles is the distinction, not the wording — both
 * sides derive it from {@link requestPhase}.
 *
 * It is a total `Record`, so the requester's list cannot gain a phase without a
 * word for it, and both bundles are asserted to carry every key
 * (`requestPhase.test.ts`) — a missing one renders as the key itself, which
 * nothing else in the build catches.
 */
const REQUESTER_PHASE_LABEL_KEY: Record<RequestPhase, string> = {
  NOT_STARTED: "endUser:myRequests.notStartedYet",
  IN_REVIEW: "endUser:myRequests.underEvaluation",
  PENDING: "endUser:myRequests.inProgressLabel",
  // The requester's words for the two halves of the pipeline. Not "approved"
  // for the second one, which is what the request technically is by then: the
  // thing they are waiting for is the access, and they have not got it yet.
  WITH_APPROVER: "endUser:myRequests.awaitingApproval",
  WITH_ACTIVATOR: "endUser:myRequests.beingSetUp",
  // "Ready", not "Approved": what the requester is told the moment access
  // lands, and the word their list has always used for it.
  APPROVED: "endUser:myRequests.ready",
  DENIED: "endUser:myRequests.denied",
  CANCELLED: "endUser:myRequests.cancelled",
  EXPIRED: "endUser:myRequests.expired",
};

/** The i18n key naming a phase to the person who filed the request. Namespaced,
 *  so it resolves from any surface. */
export function requesterPhaseLabelKey(phase: RequestPhase): string {
  return REQUESTER_PHASE_LABEL_KEY[phase];
}
